import { router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useBasePath } from '@/lib/paths'
import { useState } from 'react'

type Props = {
  nominee_director: { id: string; type_name: string }
}

export default function ProtectedPersonsNew({ nominee_director }: Props) {
  const basePath = useBasePath()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    full_name: '',
    legal_name: '',
    email: '',
    phone: '',
    person_kind: 'individual',
    nationality: '',
    country_code: '',
    state_province: '',
    city: '',
    postal_code: '',
    street_address_1: '',
    street_address_2: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    router.post(
      `${basePath}/nominee_directors/${nominee_director.id}/protected_persons`,
      { protected_person: formData },
      { onFinish: () => setIsSubmitting(false) }
    )
  }

  return (
    <DashboardLayout
      title="Add Protected Person"
      breadcrumbs={[
        { label: 'Nominee Directors', href: 'nominee_directors' },
        { label: nominee_director.type_name, href: `nominee_directors/${nominee_director.id}` },
        { label: 'Add Person' },
      ]}
    >
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Add Protected Person</h1>
        <p className="text-sm text-muted-foreground">Add a person who the nominee director will represent</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">1</span>
            <h2 className="text-base font-medium text-foreground">Person Type</h2>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Type</Label>
            <Select
              value={formData.person_kind}
              onValueChange={(value) => setFormData({ ...formData, person_kind: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="entity">Entity</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">2</span>
            <h2 className="text-base font-medium text-foreground">Details</h2>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder={formData.person_kind === 'entity' ? 'Entity name' : 'Full name'}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="legal_name">Legal Name <span className="text-muted-foreground font-normal">(Optional)</span></Label>
              <Input
                id="legal_name"
                value={formData.legal_name}
                onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                placeholder="DBA or alternate name"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="nationality">Nationality <span className="text-muted-foreground font-normal">(Optional)</span></Label>
              <Input
                id="nationality"
                value={formData.nationality}
                onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                placeholder="e.g. American, British, Nigerian"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone"
                />
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">3</span>
            <h2 className="text-base font-medium text-foreground">Address</h2>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="street_address_1">Street Address</Label>
              <Input
                id="street_address_1"
                value={formData.street_address_1}
                onChange={(e) => setFormData({ ...formData, street_address_1: e.target.value })}
                placeholder="Street address"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="street_address_2">Apt, Suite, etc. <span className="text-muted-foreground font-normal">(Optional)</span></Label>
              <Input
                id="street_address_2"
                value={formData.street_address_2}
                onChange={(e) => setFormData({ ...formData, street_address_2: e.target.value })}
                placeholder="Apt, suite, etc."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="state_province">State/Province</Label>
                <Input
                  id="state_province"
                  value={formData.state_province}
                  onChange={(e) => setFormData({ ...formData, state_province: e.target.value })}
                  placeholder="State"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  placeholder="ZIP code"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="country_code">Country Code</Label>
                <Input
                  id="country_code"
                  value={formData.country_code}
                  onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                  placeholder="US, CA, GB, etc."
                />
              </div>
            </div>
          </div>
        </section>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => router.visit(`${basePath}/nominee_directors/${nominee_director.id}`)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="flex-1">
            {isSubmitting ? 'Adding...' : 'Add Person'}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  )
}
